import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const fetchTables = (tableId) => {
    const params = {
        model: `Restaurant Object`,
        name: tableId,
        method: `orders_list`,
        args: "{}",
    };
    return axios.get(`method/frappe_helper.api.call`, { params });
};

export const useTables = (tableId, onSuccess, onError) => {
    return useQuery(["getTableOrders", tableId], () => fetchTables(tableId), {
        onSuccess,
        onError,
        enabled: !!tableId,
        cacheTime: 0,
    });
};

const fetchOrder = (name) => {
    const params = {
        model: `Restaurant Object`,
        name: name,
        method: `get`,
    };
    return axios.get(`resource/Table Order/` + name);
};

export const useOrderDetails = (name, onSuccess, onError) => {
    return useQuery(["getTableOrders", name], () => fetchOrder(name), {
        onSuccess,
        onError,
        enabled: !!name,
        cacheTime: 0,
    });
};

const createTable = (room, tableFormData) => {
    return axios.post(
        `method/restaurant_management.api.create_table`,
        JSON.stringify(tableFormData)
    );
};

export const useCreateTable = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ room, tableFormData }) => {
            const data = await createTable(room, tableFormData);
            return data;
        },
        {
            onSuccess: (data) => {
                // Refresh the query for the table order items.
                queryClient.refetchQueries(["getRestaurantTables"]);
            },
        }
    );
};

const updateTable = (tableId, tableFormData) => {
    const params = {
        desk_form: `restaurant-table`,
        data: tableFormData,
        doc_name: tableId,
    };
    return axios.get(
        `method/frappe_helper.frappe_helper.doctype.desk_form.desk_form.accept`,
        { params }
    );
};

export const useUpdateTable = (tableId, tableFormData) => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ tableId, tableFormData }) => {
            const data = await updateTable(tableFormData);
            return data;
        },
        {
            onSuccess: (data) => {
                // Refresh the query for the table order items.
                queryClient.refetchQueries(["getRestaurantTables"]);
            },
        }
    );
};

const updateTableStyle = (tableId, newStyle) => {
    let params = {
        model: `Restaurant Object`,
        method: "set_style",
        args: JSON.stringify(newStyle),
        name: tableId,
    };
    return axios.post(`method/frappe_helper.api.call`, params);
};

export const useUpdateTableStyle = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ tableId, newStyle }) => {
            const data = await updateTableStyle(tableId, newStyle);
            return data;
        },
        {
            onSuccess: (data) => {
                // Refresh the query for the table order items.
                queryClient.refetchQueries(["getRestaurantTables"]);
            },
        }
    );
};

const fetchTableDetails = (tableId) => {
    const params = {
        model: `Restaurant Object`,
        name: tableId,
        method: `get_objects`,
        args: "{}",
    };
    return axios.get(`method/frappe_helper.api.call`, { params });
};

export const useTableData = (tableId, onSuccess, onError) => {
    return useQuery(
        ["getTableDetails", tableId],
        () => fetchTableDetails(tableId),
        {
            onSuccess,
            onError,
            enabled: !!tableId,
        }
    );
};

const cleanUpTablesOrders = () => {
    return axios.get(`/method/custom_api.api.cleanup`);
};

export const usePrintOrder = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ doctype, orderId, printFormat }) => {
            let params = {
                doctype: doctype ? doctype : `Table Order`,
                printer: "Cash",
                docname: orderId,
                pf: printFormat,
            };
            const data = await axios.post(
                "/method/restaurant_management.api.print_doc",
                params
            );
            return data;
        },
        {
            onSuccess: (data) => {
                // Refresh the query for the table order items.
                queryClient.refetchQueries(["getRestaurantTables"]);
            },
        }
    );
};



const fetchAllTables = () => {
    const params = {
        fields: JSON.stringify(["*"]),
        filters: JSON.stringify([["type", "=", "Table"]]),
        limit: 0, // Set the limit to 0 to load all items
    };

    return axios.get(`/resource/Restaurant Object`, { params });
};

export const useGetAllTables = (onSuccess, onError) => {
    return useQuery(
        ["getAllTables"],
        async () => {
            const data = await fetchAllTables();
            return data.data.data;
        },
        {
            onSuccess,
            onError,
            cacheTime: 30 * 1000 * 60 * 60 * 24,
        }
    );
};
